import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setValidatingToken } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Set user from localStorage first for immediate UI render
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Mark that we're validating token (prevents api.js from redirecting)
          setValidatingToken(true);
          
          // Then verify token in background
          const res = await authAPI.getMe();
          if (res.data) {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
          }
        } catch (error) {
          // Only logout if it's a 401 error (invalid/expired token)
          if (error.response?.status === 401) {
            console.log('Token expired or invalid, logging out');
            logout();
          } else {
            // For network errors, keep the stored user (offline support)
            console.warn('Network error during auth check, keeping stored session');
          }
        } finally {
          // Always reset the validating flag
          setValidatingToken(false);
        }
      }
      
      setLoading(false);
      setInitialized(true);
    };

    initAuth();
  }, [logout]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, fullName, phone = null, country = null) => {
    const response = await authAPI.register({ 
      email, 
      password, 
      full_name: fullName,
      phone,
      country
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (e) {
      console.error('Failed to refresh user', e);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser, initialized }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
